import { useEffect, useState } from 'react';
import { TextField, MenuItem, Stack, Typography } from '@mui/material';
import { useTenantTimeFormat } from '../stores/tenant.store';

/**
 * A date + time picker whose CLOCK follows the tenant's `timeFormat` (24h vs 12h),
 * unlike the native `<input type="datetime-local">` whose AM/PM chrome follows the
 * browser locale. The value contract is identical to the native input — a tenant-local
 * wall-clock `yyyy-MM-ddTHH:mm` (24h internally) — so callers and the server are
 * unchanged; only the widget differs.
 *
 * The calendar stays a native `<input type="date">` (its value `yyyy-MM-dd` is
 * unambiguous); the time is rendered with our own selects so we control 24h/12h.
 * The hour/minute are held in local state so they can be set BEFORE a date and are
 * not lost while the emitted value is still empty (a value needs a date to be valid).
 */
export interface TenantDateTimeFieldProps {
  label?: string;
  /** `yyyy-MM-ddTHH:mm` (24h) or '' when empty. */
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  error?: boolean;
  helperText?: string;
  /** Minimum date for the calendar (`yyyy-MM-dd`). */
  minDate?: string;
}

const pad2 = (n: number): string => String(n).padStart(2, '0');

interface Parts {
  date: string;
  h: number; // 0..23
  m: number; // 0..59
}

function parse(value: string): Parts {
  const [date = '', time = ''] = value.split('T');
  const [hh = '', mm = ''] = time.split(':');
  return { date, h: Number(hh) || 0, m: Number(mm) || 0 };
}

/** Compose a 24h wall-clock from parts; empty date → '' (matches the native input). */
function compose(p: Parts): string {
  if (!p.date) return '';
  return `${p.date}T${pad2(p.h)}:${pad2(p.m)}`;
}

/** 12h hour (1–12) + period → 24h hour (0–23). */
function to24h(hour12: number, period: 'AM' | 'PM'): number {
  if (period === 'AM') return hour12 === 12 ? 0 : hour12;
  return hour12 === 12 ? 12 : hour12 + 12;
}

export function TenantDateTimeField({
  label,
  value,
  onChange,
  disabled = false,
  size = 'small',
  fullWidth = false,
  error = false,
  helperText,
  minDate,
}: TenantDateTimeFieldProps): JSX.Element {
  const timeFormat = useTenantTimeFormat();
  const is12h = timeFormat === '12h';

  // Local parts — seeded from `value`, kept in sync when `value` changes externally
  // (prefill / reset), but preserved while the user sets a time before the date.
  const [parts, setParts] = useState<Parts>(() => parse(value));
  useEffect(() => {
    if (value !== compose(parts)) setParts(parse(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const update = (next: Parts) => {
    setParts(next);
    onChange(compose(next));
  };

  const { date, h, m } = parts;
  const period: 'AM' | 'PM' = h < 12 ? 'AM' : 'PM';
  const hour12 = h % 12 === 0 ? 12 : h % 12;

  const hourOptions = is12h
    ? Array.from({ length: 12 }, (_, i) => i + 1) // 1..12
    : Array.from({ length: 24 }, (_, i) => i); // 0..23
  const hourValue = is12h ? hour12 : h;

  const onHour = (raw: string) => {
    const n = Number(raw);
    update({ ...parts, h: is12h ? to24h(n, period) : n });
  };
  // Selects keep a fixed width and never shrink; the date field takes the rest so a
  // narrow container can't squeeze the hour/minute selects into "0…".
  const selectSx = { width: is12h ? 68 : 76, flexShrink: 0 } as const;

  return (
    <Stack spacing={0.5} sx={{ minWidth: 0, ...(fullWidth ? { width: '100%' } : {}) }}>
      {label && (
        <Typography variant="caption" color={error ? 'error' : 'text.secondary'}>
          {label}
        </Typography>
      )}
      <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
        <TextField
          type="date"
          value={date}
          onChange={(e) => update({ ...parts, date: e.target.value })}
          disabled={disabled}
          size={size}
          error={error}
          sx={{ flex: fullWidth ? 1 : '0 0 auto', minWidth: 150 }}
          {...(minDate ? { inputProps: { min: minDate } } : {})}
        />
        <TextField
          select
          aria-label="hora"
          value={hourValue}
          onChange={(e) => onHour(e.target.value)}
          disabled={disabled}
          size={size}
          sx={selectSx}
        >
          {hourOptions.map((hv) => (
            <MenuItem key={hv} value={hv}>
              {is12h ? hv : pad2(hv)}
            </MenuItem>
          ))}
        </TextField>
        <Typography component="span" sx={{ color: 'text.secondary', mx: -0.25 }}>
          :
        </Typography>
        <TextField
          select
          aria-label="minutos"
          value={m}
          onChange={(e) => update({ ...parts, m: Number(e.target.value) })}
          disabled={disabled}
          size={size}
          sx={selectSx}
        >
          {Array.from({ length: 60 }, (_, i) => i).map((mv) => (
            <MenuItem key={mv} value={mv}>
              {pad2(mv)}
            </MenuItem>
          ))}
        </TextField>
        {is12h && (
          <TextField
            select
            aria-label="AM/PM"
            value={period}
            onChange={(e) => update({ ...parts, h: to24h(hour12, e.target.value as 'AM' | 'PM') })}
            disabled={disabled}
            size={size}
            sx={{ width: 72, flexShrink: 0 }}
          >
            <MenuItem value="AM">AM</MenuItem>
            <MenuItem value="PM">PM</MenuItem>
          </TextField>
        )}
      </Stack>
      {helperText && (
        <Typography variant="caption" color={error ? 'error' : 'text.secondary'}>
          {helperText}
        </Typography>
      )}
    </Stack>
  );
}
